import { Link } from "react-router-dom";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useInvoices } from "../hooks/useInvoices";
import {
  DeleteInvoiceModal,
  EditInvoiceModal,
} from "../components/InvoiceActionsModal";
import EmitirNotaCreditoModal from "../components/EmitirNotaCreditoModal";

import TableHeader from "../components/TableHeader";
import TableBody from "../components/TableBody";
import TableFooter from "../components/TableFooter";
import ImportExportBar from "../../shared/ImportExportBar";
import SendEmailModal from "../components/SendEmailModal";
import apiFetch from "../../../../shared/services/apiClient";

import styles from "../styles/invoice.module.css";

const INITIAL_PARAMS = {
  search: "",
  fecha_desde: "",
  fecha_hasta: "",
  monto_min: "",
  monto_max: "",
  rubro: "",
  ordering: "",
};

export default function ListInvoice() {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [params, setParams] = useState(INITIAL_PARAMS);
  const [importResult, setImportResult] = useState(null);

  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [emitiendoNC, setEmitiendoNC] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [emailModal, setEmailModal] = useState(null);

  // ── Selección cross-page: Map<id, facturaObj> ──────────────────────
  const [selectedMap, setSelectedMap] = useState(new Map());
  const [selectingAll, setSelectingAll] = useState(false);

  const selectedIds = new Set(selectedMap.keys());

  function toggleSelect(id, invoiceObj) {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, invoiceObj);
      return next;
    });
  }

  // Seleccionar TODAS las facturas del filtro actual (todas las páginas)
  async function selectAll() {
    setSelectingAll(true);
    try {
      const qs = buildAllQS(params);
      const res = await apiFetch(`/api/finance/factura/?${qs}`);
      const data = await res.json();
      const all = data.results ?? [];
      const map = new Map();
      all.forEach((inv) => map.set(inv.id_factura ?? inv.id, inv));
      setSelectedMap(map);
    } catch (_) {
    } finally {
      setSelectingAll(false);
    }
  }

  function clearSelection() {
    setSelectedMap(new Map());
  }

  function handleParamsChange(newParams) {
    setParams(newParams);
    setPage(1);
    clearSelection();
  }

  const { invoices, count, next, previous, loading, total } = useInvoices(
    page,
    params,
    refreshKey,
  );
  const totalPages = Math.ceil(count / 8);

  const handleImportSuccess = useCallback(() => {
    setPage(1);
    setRefreshKey((k) => k + 1);
  }, []);

  function handleResult(resultado) {
    setImportResult(resultado);
  }

  function handleDeleteSuccess() {
    setRefreshKey((k) => k + 1);
    setActionMsg({ ok: true, text: t("facturas.facturaEliminada") });
    setTimeout(() => setActionMsg(null), 4000);
  }

  function handleEditSuccess(warning) {
    setRefreshKey((k) => k + 1);
    if (warning) {
      setActionMsg({ ok: false, text: warning });
    } else {
      setActionMsg({ ok: true, text: t("facturas.facturaActualizada") });
      setTimeout(() => setActionMsg(null), 4000);
    }
  }

  function handleEmitirNCSuccess(nc) {
    setRefreshKey((k) => k + 1);
    setActionMsg({
      ok: true,
      text: t("notasCredito.emitida", { numero: nc?.numero_nota ?? "" }),
    });
    setTimeout(() => setActionMsg(null), 4000);
  }

  return (
    <>
      {/* ── Breadcrumb ─────────────────────────────────────────────── */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxc">
          <span className={styles.inactive}> {t("facturas.cuentasPorCobrar")} /</span>
        </Link>
        <span className={styles.active}> {t("facturas.titulo")}</span>
      </span>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ marginBottom: "6px" }}
      >
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("facturas.gestion")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("facturas.administra")}</p>
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
            <ImportExportBar
              plantillaUrl="/api/finance/factura/plantilla-excel/"
              importarUrl="/api/finance/factura/importar-excel/"
              exportarUrl="/api/finance/factura/exportar-excel/"
              plantillaFilename="plantilla_facturas.xlsx"
              exportarFilename="facturas.xlsx"
              onImportSuccess={handleImportSuccess}
              onResult={handleResult}
            />
            <Link
              to="/finanzas/facturas/agregar"
              className={`btn ${styles["btn-add-factura"]} d-flex gap-2 align-items-center`}
            >
              <i className="fa-solid fa-plus" /> {t("facturas.agregarFactura")}
            </Link>

            <Link
              to="/finanzas/facturas/masivo"
              className={`btn ${styles["btn-add-factura"]} d-flex gap-2 align-items-center`}
            >
              <i className="fa-solid fa-layer-group" /> {t("facturas.facturasMasivas")}
            </Link>
          </div>
          <div className="d-flex gap-2 align-items-center justify-content-end flex-wrap">
            {selectedIds.size > 0 && (
              <>
                <span style={{ color: "var(--text-secondary)", fontSize: "0.82rem" }}>
                  {selectedIds.size} {t("common.seleccionados")}
                </span>

                <button
                  className="btn"
                  onClick={clearSelection}
                  style={{
                    background: "transparent",
                    color: "var(--text-secondary)",
                    border: "1px solid var(--border-card)",
                    fontSize: "0.8rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#ef4444";
                    e.currentTarget.style.color = "#ef4444";
                    e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-card)";
                    e.currentTarget.style.color = "var(--text-secondary)";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{" "}
                  {t("common.deseleccionarTodas")}
                </button>
                <button
                  className="btn d-flex gap-2 align-items-center"
                  onClick={() =>
                    setEmailModal(Array.from(selectedMap.values()))
                  }
                  style={{
                    background: "#f97316",
                    color: "#fff",
                    border: "1px solid #f97316",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#ea580c")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f97316")
                  }
                >
                  <i className="fa-solid fa-envelope" /> {t("common.enviarCorreos")} (
                  {selectedIds.size})
                </button>
              </>
            )}
            {selectedIds.size !== count && (
              <button
                className="btn d-flex gap-2 align-items-center"
                onClick={selectAll}
                disabled={selectingAll || count === 0}
                style={{
                  background: "var(--hover-bg)",
                  color: "var(--text-secondary)",
                  border: "none",
                  fontSize: "0.8rem",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                onMouseOut={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
              >
                {selectingAll ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin" />{" "}
                    {t("common.seleccionando")}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check-double" /> {t("common.seleccionarTodas")}
                    ({count})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Resultado importación ──────────────────────────────────── */}
      {importResult && (
        <div
          style={{
            width: "100%",
            borderRadius: "8px",
            marginBottom: "14px",
            background: importResult.ok
              ? "rgba(74,222,128,0.07)"
              : "rgba(248,113,113,0.07)",
            border: `1px solid ${importResult.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
            borderLeft: `4px solid ${importResult.ok ? "#4ade80" : "#f87171"}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 16px",
              borderBottom:
                importResult.errores?.length > 0
                  ? "1px solid var(--border-card)"
                  : "none",
            }}
          >
            <span
              style={{
                fontWeight: 600,
                fontSize: "0.88rem",
                color: importResult.ok ? "#bbf7d0" : "#fecaca",
              }}
            >
              {importResult.msg}
            </span>
            <button
              onClick={() => setImportResult(null)}
              style={{
                background: "none",
                border: "none",
                color: "var(--text-muted)",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              ✕
            </button>
          </div>
          {importResult.errores?.length > 0 && (
            <div style={{ padding: "10px 16px" }}>
              <details>
                <summary
                  style={{
                    cursor: "pointer",
                    color: "#fb923c",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                  }}
                >
                  {importResult.errores.length} {t("recibos.filasConErrores")}
                </summary>
                <ul
                  style={{
                    marginTop: 8,
                    paddingLeft: 18,
                    color: "#fca5a5",
                    fontSize: "0.8rem",
                  }}
                >
                  {importResult.errores.map((e, i) => (
                    <li key={i}>
                      <strong>{t("common.filas")} {e.fila}:</strong>{" "}
                      {e.errores?.join(", ") ?? e}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      )}

      {/* ── Tabla principal ────────────────────────────────────────── */}
      <div className={styles["table-container"]}>
        <TableHeader
          params={params}
          onParamsChange={handleParamsChange}
          loading={loading}
        />
        <TableBody
          invoices={invoices}
          onEdit={(inv) => setEditingInvoice(inv)}
          onDelete={(inv) => setDeletingInvoice(inv)}
          onEmail={(inv) => setEmailModal([inv])}
          onEmitirNC={(inv) => setEmitiendoNC(inv)}
          selectedIds={selectedIds}
          onToggleSelect={(id, inv) => toggleSelect(id, inv)}
        />
        <TableFooter
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          next={next}
          previous={previous}
          count={count}
          loading={loading}
          total={total}
        />
      </div>

      {/* ── Modales ────────────────────────────────────────────────── */}
      {emailModal && (
        <SendEmailModal
          facturas={emailModal}
          onClose={() => setEmailModal(null)}
          onSuccess={() => {
            setActionMsg({ ok: true, text: t("common.correosEnviados") });
            setTimeout(() => setActionMsg(null), 4000);
            clearSelection();
          }}
        />
      )}

      {deletingInvoice && (
        <DeleteInvoiceModal
          invoice={deletingInvoice}
          onClose={() => setDeletingInvoice(null)}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {editingInvoice && (
        <EditInvoiceModal
          invoice={editingInvoice}
          onClose={() => setEditingInvoice(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {emitiendoNC && (
        <EmitirNotaCreditoModal
          invoice={emitiendoNC}
          onClose={() => setEmitiendoNC(null)}
          onSuccess={handleEmitirNCSuccess}
        />
      )}

      {/* ── Toast flotante ─────────────────────────────────────────── */}
      {actionMsg && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(251,146,60,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(251,146,60,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#fb923c"}`,
          borderRadius: 8, padding: "12px 18px",
          display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380,
        }}>
          <i
            className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`}
            style={{ color: actionMsg.ok ? "#4ade80" : "#fb923c", fontSize: "1.1rem" }}
          />
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#fb923c", fontWeight: 600, fontSize: "0.88rem" }}>
            {actionMsg.text}
          </span>
        </div>
      )}
    </>
  );
}

// Construye query string sin paginación para traer todas las facturas del filtro
function buildAllQS(params) {
  const p = new URLSearchParams();
  p.set("page_size", "9999");
  if (params.search?.trim()) p.set("search", params.search.trim());
  if (params.fecha_desde?.trim())
    p.set("fecha_desde", params.fecha_desde.trim());
  if (params.fecha_hasta?.trim())
    p.set("fecha_hasta", params.fecha_hasta.trim());
  if (params.monto_min?.trim()) p.set("monto_min", params.monto_min.trim());
  if (params.monto_max?.trim()) p.set("monto_max", params.monto_max.trim());
  if (params.rubro?.toString().trim()) p.set("rubro", params.rubro.toString().trim());
  if (params.ordering?.trim()) p.set("ordering", params.ordering.trim());
  return p.toString();
}
